import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ArrowRight, Phone, MessageCircle, Users, Shield, Clock, Award, CheckCircle } from 'lucide-react';
import MapSection from '@/components/MapSection';
import { useLanguage } from '@/context/LanguageContext';

const HERO_IMAGE = "https://customer-assets.emergentagent.com/job_my-digital-home-7/artifacts/euqae73q_749E8A3B-6C0F-4FE8-923E-7AB0DEE1C36A.png";

const HomePage = () => {
  const { t, isRTL } = useLanguage();

  const services = [
    { name: t('services.nannies.title').split(' ')[0], icon: '👶' },
    { name: t('services.housemaids.title').split(' ')[0], icon: '🏠' },
    { name: t('services.security.title').split(' ')[0], icon: '🛡️' },
    { name: t('services.cooks.title').split(' ')[0], icon: '👨‍🍳' },
    { name: t('services.drivers.title').split(' ')[0], icon: '🚗' },
    { name: t('services.teachers.title').split(' ')[0], icon: '📚' },
    { name: t('services.caregivers.title').split(' ')[0], icon: '❤️' },
    { name: t('services.farmers.title').split(' ')[0], icon: '🌾' },
  ];

  const whyChooseUs = [
    { icon: Shield, ...t('whyChoose.verified') },
    { icon: Clock, ...t('whyChoose.quick') },
    { icon: Users, ...t('whyChoose.skilled') },
    { icon: Award, ...t('whyChoose.quality') },
  ];

  const stats = [
    { value: '500+', label: t('stats.happyClients') },
    { value: '1000+', label: t('stats.staffPlaced') },
    { value: '10+', label: t('stats.yearsExp') },
    { value: '24/7', label: t('stats.support') },
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section - Using Exact Provided Image */}
      <section className="relative min-h-screen flex items-center justify-center pt-20">
        {/* Hero Image - Full Width */}
        <div className="absolute inset-0 z-0">
          <img
            src={HERO_IMAGE}
            alt="TDABYR ALMSTQBL - Domestic & Labor Services"
            className="w-full h-full object-cover object-top"
          />
          {/* Gradient Overlay for readability */}
          <div className="absolute inset-0 bg-gradient-to-t from-background via-background/60 to-transparent" />
        </div>

        {/* Hero Content */}
        <div className="relative z-10 container-custom text-center mt-auto pb-20">
          <div className="max-w-4xl mx-auto space-y-8 animate-fade-in-up">
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-foreground leading-tight">
              {t('home.heroTitle')}{' '}
              <span className="text-gradient-uae">{t('home.heroTitleHighlight')}</span>
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto">
              {t('home.heroSubtitle')}
            </p>
            
            {/* CTA Buttons */}
            <div className={`flex flex-col sm:flex-row items-center justify-center gap-4 pt-4 ${isRTL ? 'sm:flex-row-reverse' : ''}`}>
              <Button
                variant="uaeRed"
                size="xl"
                className="w-full sm:w-auto gap-2"
                onClick={() => window.open('https://wa.me/97167796889', '_blank')}
              >
                <MessageCircle className="w-5 h-5" />
                {t('home.getQuoteWhatsApp')}
              </Button>
              <Button
                variant="outlineGreen"
                size="xl"
                className="w-full sm:w-auto gap-2"
                onClick={() => window.open('tel:+97167796889', '_self')}
              >
                <Phone className="w-5 h-5" />
                {t('home.callNow')}
              </Button>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-muted-foreground rounded-full flex items-start justify-center p-2">
            <div className="w-1 h-2 bg-muted-foreground rounded-full" />
          </div>
        </div>
      </section>

      {/* Services Overview */}
      <section className="section-padding bg-card">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="font-display text-3xl md:text-4xl font-bold text-foreground mb-4">
              {t('home.ourServices')}
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              {t('home.servicesSubtitle')}
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-4">
            {services.map((service, index) => (
              <Link
                key={index}
                to="/services"
                className="group"
              >
                <Card className="bg-muted/50 border-border hover:border-uae-green card-hover cursor-pointer">
                  <CardContent className="p-6 text-center">
                    <div className="text-4xl mb-3">{service.icon}</div>
                    <p className="text-sm font-medium text-foreground group-hover:text-uae-green transition-colors">
                      {service.name}
                    </p>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>

          <div className="text-center mt-12">
            <Button variant="uaeGreen" size="lg" asChild>
              <Link to="/services" className="gap-2">
                {t('home.viewAllServices')}
                <ArrowRight className={`w-4 h-4 ${isRTL ? 'rotate-180' : ''}`} />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="section-padding">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="font-display text-3xl md:text-4xl font-bold text-foreground mb-4">
              {t('home.whyChooseUs')} <span className="text-uae-red">{t('home.whyChooseUsHighlight')}</span>
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              {t('home.whyChooseUsSubtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {whyChooseUs.map((item, index) => (
              <Card
                key={index}
                className="bg-card border-border card-hover group"
              >
                <CardContent className="p-8 text-center">
                  <div className="w-16 h-16 mx-auto mb-6 rounded-xl bg-muted flex items-center justify-center group-hover:bg-uae-red/10 transition-colors">
                    <item.icon className="w-8 h-8 text-uae-red" />
                  </div>
                  <h3 className="font-display text-xl font-semibold text-foreground mb-3">
                    {item.title}
                  </h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">
                    {item.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-gradient-to-r from-uae-red to-uae-green">
        <div className="container-custom">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat) => (
              <div key={stat.label} className="text-center">
                <div className="font-display text-4xl md:text-5xl font-bold text-uae-white mb-2">
                  {stat.value}
                </div>
                <p className="text-uae-white/80 text-sm md:text-base">
                  {stat.label}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding">
        <div className="container-custom">
          <Card className="bg-card border-border overflow-hidden">
            <CardContent className="p-0">
              <div className={`grid lg:grid-cols-2 gap-0 ${isRTL ? 'lg:grid-flow-dense' : ''}`}>
                {/* Content Side */}
                <div className="p-8 md:p-12 lg:p-16 flex flex-col justify-center">
                  <h2 className="font-display text-3xl md:text-4xl font-bold text-foreground mb-6">
                    {t('home.readyToFind')}{' '}
                    <span className="text-uae-green">{t('home.perfectStaff')}</span>
                  </h2>
                  <ul className="space-y-4 mb-8">
                    {[
                      t('home.freeConsultation'),
                      t('home.quickPlacement'),
                      t('home.replacementGuarantee'),
                      t('home.support247'),
                    ].map((item) => (
                      <li key={item} className={`flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                        <CheckCircle className="w-5 h-5 text-uae-green flex-shrink-0" />
                        <span className="text-muted-foreground">{item}</span>
                      </li>
                    ))}
                  </ul>
                  <div className={`flex flex-col sm:flex-row gap-4 ${isRTL ? 'sm:flex-row-reverse' : ''}`}>
                    <Button
                      variant="uaeRed"
                      size="lg"
                      className="gap-2"
                      onClick={() => window.open('https://wa.me/97167796889', '_blank')}
                    >
                      <MessageCircle className="w-5 h-5" />
                      {t('home.whatsAppUs')}
                    </Button>
                    <Button
                      variant="outlinePremium"
                      size="lg"
                      asChild
                    >
                      <Link to="/contact">{t('home.contactUs')}</Link>
                    </Button>
                  </div>
                </div>

                {/* Decorative Side */}
                <div className={`relative hidden lg:block bg-gradient-to-br from-uae-red/20 to-uae-green/20 ${isRTL ? 'lg:col-start-1' : ''}`}>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="text-center">
                      <div className="font-display text-6xl font-bold text-foreground/10">
                        TDABYR
                      </div>
                      <div className="font-display text-4xl font-bold text-foreground/10">
                        ALMSTQBL
                      </div>
                    </div>
                  </div>
                  {/* UAE Flag Colors Accent */}
                  <div className="absolute bottom-0 left-0 right-0 h-2 flex">
                    <div className="flex-1 bg-uae-red" />
                    <div className="flex-1 bg-uae-green" />
                    <div className="flex-1 bg-foreground" />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Google Maps Section */}
      <MapSection />
    </div>
  );
};

export default HomePage;
